/**
 * SOLID Review
 * S — Strong. One concern: the table's video chat decisions, each built from the snapshot as a `MediaPolicy`, asked,
 *     and answered through a `ClientDecision`. The rules — the idle rule, presence once per kind — are the policy's.
 * O — Good. A new reason to turn video off is a method here, a row in the table and its move on the policy.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the media ops' table and nothing else.
 * D — Strong. The media policy, the account value object, the decision builder and the schemas.
 */
import { MediaPolicy } from '../../media/MediaPolicy';
import { Account } from '../../session/Account';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type {
  ICommandMediaClock, ICommandMediaIdle, ICommandMediaBanner, ICommandMediaUsed, IEntityPresencePlan,
} from '../../ports/schemas/MediaMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TMediaOp = 'mediaToggled' | 'mediaReconnect' | 'mediaIdleChecked' | 'mediaRemovedByServer' | 'mediaBannerDismissed' | 'planPresence';

/**
 * THE TABLE'S VIDEO CHAT (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide functions of
 * `decisions/media/mediaPolicy.ts` (FE5c, useLiveKitSession's) as a service. A move at no table changes nothing.
 */
export class Media implements IServiceDecisionTable<TMediaOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TMediaOp> = {
    mediaToggled: (input, command) => this.mediaToggled(input, command),
    mediaReconnect: (input, command) => this.mediaReconnect(input, command),
    mediaIdleChecked: (input, command) => this.mediaIdleChecked(input, command),
    mediaRemovedByServer: (input) => this.mediaRemovedByServer(input),
    mediaBannerDismissed: (input, command) => this.mediaBannerDismissed(input, command),
    planPresence: (input, command) => this.planPresence(input, command),
  };

  /** The 📡 control. */
  mediaToggled(input: IPropsClientInput, command: ICommandMediaClock): TClientDecision<IEntityTableAck> {
    const media = MediaPolicy.atTable(input);
    return ClientDecision.keeping(input, media).acked(media.toggled(command.now));
  }

  /** The idle banner's or badge's Reconnect. */
  mediaReconnect(input: IPropsClientInput, command: ICommandMediaClock): TClientDecision<IEntityTableAck> {
    const media = MediaPolicy.atTable(input);
    return ClientDecision.keeping(input, media).acked(media.reconnected(command.now));
  }

  /** The idle clock ticked. */
  mediaIdleChecked(input: IPropsClientInput, command: ICommandMediaIdle): TClientDecision<IEntityTableAck> {
    const media = MediaPolicy.atTable(input);
    return ClientDecision.keeping(input, media).acked(media.idleChecked(command));
  }

  /** The server's idle sweep removed this player. */
  mediaRemovedByServer(input: IPropsClientInput): TClientDecision<IEntityTableAck> {
    const media = MediaPolicy.atTable(input);
    return ClientDecision.keeping(input, media).acked(media.removedByServer());
  }

  /** The idle banner's ✕, or a fresh disconnect. */
  mediaBannerDismissed(input: IPropsClientInput, command: ICommandMediaBanner): TClientDecision<IEntityTableAck> {
    const media = MediaPolicy.atTable(input);
    return ClientDecision.keeping(input, media).acked(media.bannerDismissed(command.dismissed));
  }

  /** A use of the microphone or the camera: the report to send, if any. */
  planPresence(input: IPropsClientInput, command: ICommandMediaUsed): TClientDecision<IEntityPresencePlan> {
    const media = MediaPolicy.inRoom(input, command.roomId);
    const plan: IEntityPresencePlan = { request: media.used(command, Account.of(input.session.account)) };
    return ClientDecision.keeping(input, media).answering(plan);
  }
}
